/**
 * SupplierRegisterPage Component
 * VROONG 협력사 회원가입 페이지
 *
 * @description
 * 협력사 계정 생성 폼
 * 협력사 ID 확인 후 담당자 정보 입력 및 유효성 검증
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ToastPosition, ToastType } from "@/shared/components/feedback/Toast/Toast.types";
import { useToast } from "@/shared/components/feedback/Toast/ToastContext";
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { extractErrorMessage } from '@/shared/utils/errorHandler';
import PasswordInput from '@/shared/components/forms/PasswordInput';
import FormInput from '@/shared/components/forms/FormInput';
import PrimaryButton from '@/shared/components/buttons/PrimaryButton';
import LinkButton from '@/shared/components/buttons/LinkButton';
import BrandSection from '../../ui/BrandSection/BrandSection';
import VersionInfo from '../../ui/VersionInfo';
import {
    UserIcon,
    EmailIcon,
    PhoneIcon,
    LoginIcon,
} from '@/shared/components/icons/Icons';
import { SupplierInfo } from '@/features/auth/types/auth.types';
import {
    validateUserName,
    validatePassword,
    validatePasswordConfirmation,
    validatePhoneNumber,
    validateEmail,
    ValidationErrors,
} from '@/features/auth/lib/validation';
import { SUPPLIER_FEATURES } from '@/features/auth/config/feature.config';
import '@/features/auth/styles/auth.css';

// ============================================================
// 상수 정의
// ============================================================

/** 회원가입 성공 후 로그인 페이지 이동 지연 시간 (ms) */
const REDIRECT_DELAY = 1500;

/** 토스트 기본 옵션 */
const TOAST_OPTIONS = {
    position: ToastPosition.TOP_CENTER,
} as const;

/** 필수 필드 마커 스타일 */
const REQUIRED_MARKER_STYLE: React.CSSProperties = {
    color: '#EF4444',
};

/** 협력사 ID 확인 버튼 기본 스타일 */
const CHECK_BUTTON_BASE_STYLE: React.CSSProperties = {
    width: '100%',
    marginTop: '0.5rem',
    padding: '0.75rem 1rem',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '0.875rem',
    fontWeight: '600',
    transition: 'all 0.3s ease',
};

/** 협력사 정보 표시 스타일 */
const SUPPLIER_INFO_STYLE: React.CSSProperties = {
    marginTop: '0.5rem',
    padding: '0.75rem',
    background: '#F0FDF9',
    borderRadius: '8px',
    fontSize: '0.875rem',
    color: '#059669',
};

/** 도움말 링크 컨테이너 스타일 */
const HELP_LINKS_STYLE: React.CSSProperties = {
    marginBottom: '5rem',
};

// ============================================================
// 컴포넌트 구현
// ============================================================

/**
 * 협력사 회원가입 페이지
 */
const SupplierRegisterPage: React.FC = () => {
    // ======== Hooks ========
    const { showToast } = useToast();
    const navigate = useNavigate();
    const location = useLocation();
    const { checkSupplierId, registerSupplier, isLoading } = useAuth();

    // location state에서 협력사 정보 가져오기
    const locationState = location.state as {
        supplierId?: string;
        supplierInfo?: SupplierInfo;
    } | null;

    // ======== State ========
    const [supplierId, setSupplierId] = useState('');
    const [supplierIdChecked, setSupplierIdChecked] = useState(false);
    const [supplierInfo, setSupplierInfo] = useState<SupplierInfo | null>(null);
    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [userName, setUserName] = useState('');
    const [phoneNum, setPhoneNum] = useState('');
    const [emailInfo, setEmailInfo] = useState('');
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState<ValidationErrors>({});

    // ======== Memoized Values ========
    /** 브랜드 섹션 설명 텍스트 */
    const brandDescription = useMemo(() => (
        <>
            VROONG 협력사로 가입합니다
            <br />
            소속 라이더 정산을 효율적으로 관리하세요
        </>
    ), []);

    /** 담당자 이름/휴대폰 필드 비활성화 여부 */
    const isInfoFieldDisabled = useMemo(() => {
        return isLoading || (locationState?.supplierInfo !== undefined && locationState?.supplierInfo !== null);
    }, [isLoading, locationState?.supplierInfo]);

    /** 협력사 ID 확인 버튼 스타일 */
    const checkButtonStyle = useMemo((): React.CSSProperties => ({
        ...CHECK_BUTTON_BASE_STYLE,
        background: supplierIdChecked ? '#10B981' : '#05CD82',
        cursor: isLoading || supplierIdChecked || !supplierId ? 'not-allowed' : 'pointer',
        opacity: (isLoading || !supplierId) && !supplierIdChecked ? 0.6 : 1,
    }), [supplierIdChecked, isLoading, supplierId]);

    // ======== Effects ========
    /** 컴포넌트 마운트 시 전달받은 협력사 정보로 초기화 */
    useEffect(() => {
        if (locationState?.supplierId && locationState?.supplierInfo) {
            setSupplierId(locationState.supplierId);
            setSupplierInfo(locationState.supplierInfo);
            setSupplierIdChecked(true);
            setUserName(locationState.supplierInfo.managerName || '');
            const phoneNumber = (locationState.supplierInfo.mngPhoneNum || '').replace(/[^0-9]/g, '');
            setPhoneNum(phoneNumber);
        }
    }, [locationState]);

    // ======== Validation ========
    /**
     * 폼 전체 유효성 검증
     */
    const validateForm = useCallback((): boolean => {
        const errors: ValidationErrors = {};

        if (!supplierIdChecked) {
            errors.supplierId = '협력사 ID 확인이 필요합니다.';
        }

        const userNameError = validateUserName(userName, 2);
        if (userNameError) errors.userName = userNameError;

        const passwordError = validatePassword(password);
        if (passwordError) errors.password = passwordError;

        const passwordConfirmError = validatePasswordConfirmation(password, passwordConfirm);
        if (passwordConfirmError) errors.passwordConfirm = passwordConfirmError;

        const phoneNumError = validatePhoneNumber(phoneNum);
        if (phoneNumError) errors.phoneNum = phoneNumError;

        const emailError = validateEmail(emailInfo, false);
        if (emailError) errors.emailInfo = emailError;

        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    }, [supplierIdChecked, userName, password, passwordConfirm, phoneNum, emailInfo]);

    // ======== Event Handlers ========
    /**
     * 협력사 ID 입력 변경 핸들러
     */
    const handleSupplierIdChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setSupplierId(e.target.value);
        setSupplierIdChecked(false);
        setSupplierInfo(null);
    }, []);

    /**
     * 협력사 ID 확인 핸들러
     */
    const handleCheckSupplierId = useCallback(async () => {
        if (!supplierId) {
            setFieldErrors(prev => ({ ...prev, supplierId: '협력사 ID를 입력해주세요.' }));
            return;
        }

        try {
            const response = await checkSupplierId(supplierId);

            if (response.exists && response.supplierInfo) {
                setSupplierIdChecked(true);
                setSupplierInfo(response.supplierInfo);
                setFieldErrors(prev => ({ ...prev, supplierId: '' }));
                setUserName(response.supplierInfo.managerName || '');
                const phoneNumber = (response.supplierInfo.mngPhoneNum || '').replace(/[^0-9]/g, '');
                setPhoneNum(phoneNumber);
                showToast(`협력사 확인 완료: ${response.supplierInfo.suppName}`, {
                    type: ToastType.SUCCESS,
                    ...TOAST_OPTIONS,
                });
            } else {
                setFieldErrors(prev => ({ ...prev, supplierId: '등록되지 않은 협력사 ID입니다.' }));
                setSupplierIdChecked(false);
                setSupplierInfo(null);
            }
        } catch (err: unknown) {
            const errorMessage = extractErrorMessage(err, '협력사 ID 확인에 실패했습니다.');
            setFieldErrors(prev => ({ ...prev, supplierId: errorMessage }));
            setSupplierIdChecked(false);
            setSupplierInfo(null);
        }
    }, [supplierId, checkSupplierId, showToast]);

    /**
     * 담당자 이름 입력 핸들러
     */
    const handleUserNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setUserName(e.target.value);
    }, []);

    /**
     * 비밀번호 입력 핸들러
     */
    const handlePasswordChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setPassword(e.target.value);
    }, []);

    /**
     * 비밀번호 확인 입력 핸들러
     */
    const handlePasswordConfirmChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setPasswordConfirm(e.target.value);
    }, []);

    /**
     * 휴대폰번호 입력 핸들러 (숫자만 허용)
     */
    const handlePhoneNumChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setPhoneNum(e.target.value.replace(/[^0-9]/g, ''));
    }, []);

    /**
     * 이메일 입력 핸들러
     */
    const handleEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setEmailInfo(e.target.value);
    }, []);

    /**
     * 회원가입 폼 제출 처리
     */
    const handleSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!validateForm()) return;

        try {
            const response = await registerSupplier({
                supplierId,
                password,
                userName,
                phoneNum,
                emailInfo: emailInfo || undefined,
            });

            if (response.success) {
                showToast('회원가입이 완료되었습니다. 로그인 페이지로 이동합니다.', {
                    type: ToastType.SUCCESS,
                    ...TOAST_OPTIONS,
                });
                setTimeout(() => navigate('/login'), REDIRECT_DELAY);
            } else {
                const errorMsg = response.message || '회원가입에 실패했습니다.';
                showToast(errorMsg, { type: ToastType.ERROR, ...TOAST_OPTIONS });
                setError(errorMsg);
            }
        } catch (err: unknown) {
            const errorMessage = extractErrorMessage(err);
            showToast(errorMessage, { type: ToastType.ERROR, ...TOAST_OPTIONS });
            setError(errorMessage);
        }
    }, [validateForm, registerSupplier, supplierId, password, userName, phoneNum, emailInfo, showToast, navigate]);

    /**
     * 계정 유형 선택 페이지 이동
     */
    const handleNavigateToRegister = useCallback(() => {
        navigate('/register');
    }, [navigate]);

    /**
     * 로그인 페이지 이동
     */
    const handleNavigateToLogin = useCallback(() => {
        navigate('/login');
    }, [navigate]);

    // ======== Render ========
    return (
        <div className="login-page">
            <div className="login-container">
                {/* 좌측 브랜드 섹션 */}
                <BrandSection
                    title="협력사 가입"
                    description={brandDescription}
                    features={SUPPLIER_FEATURES}
                />

                {/* 우측 회원가입 폼 섹션 */}
                <div className="login-section">
                    <div className="login-form-container">
                        {/* 헤더 */}
                        <div className="login-header">
                            <h2>협력사 회원가입</h2>
                            <p>협력사 정보를 확인하고 계정을 생성하세요</p>
                        </div>

                        {/* 회원가입 폼 */}
                        <form className="login-form" onSubmit={handleSubmit}>
                            {/* 협력사 ID */}
                            <div className="form-group">
                                <label htmlFor="supplierId" className="form-label">
                                    협력사 ID <span style={REQUIRED_MARKER_STYLE}>*</span>
                                </label>
                                <div className="input-wrapper">
                                    <svg
                                        className="input-icon"
                                        width="20"
                                        height="20"
                                        fill="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                    </svg>
                                    <input
                                        type="text"
                                        id="supplierId"
                                        name="supplierId"
                                        className="form-input"
                                        placeholder="협력사 ID를 입력하세요"
                                        value={supplierId}
                                        onChange={handleSupplierIdChange}
                                        required
                                        disabled
                                    />
                                </div>
                                {!supplierIdChecked && (
                                    <button
                                        type="button"
                                        onClick={handleCheckSupplierId}
                                        disabled={isLoading || supplierIdChecked}
                                        style={checkButtonStyle}
                                    >
                                        {supplierIdChecked ? '✓ 확인완료' : '협력사 ID 확인'}
                                    </button>
                                )}
                                {fieldErrors.supplierId && (
                                    <div className="error-message">{fieldErrors.supplierId}</div>
                                )}
                                {supplierInfo && (
                                    <div style={SUPPLIER_INFO_STYLE}>
                                        <strong>{supplierInfo.suppName}</strong> ({supplierInfo.suppCompName})
                                    </div>
                                )}
                            </div>

                            {/* 담당자 이름 */}
                            <FormInput
                                id="userName"
                                name="userName"
                                value={userName}
                                onChange={handleUserNameChange}
                                placeholder="담당자 이름을 입력하세요"
                                label="담당자 이름"
                                required
                                disabled={isInfoFieldDisabled}
                                icon={<UserIcon />}
                                error={fieldErrors.userName}
                            />

                            {/* 비밀번호 */}
                            <PasswordInput
                                id="password"
                                name="password"
                                value={password}
                                onChange={handlePasswordChange}
                                placeholder="8자 이상, 영문/숫자/특수문자 포함"
                                required
                                disabled={isLoading}
                                error={fieldErrors.password}
                            />

                            {/* 비밀번호 확인 */}
                            <PasswordInput
                                id="passwordConfirm"
                                name="passwordConfirm"
                                value={passwordConfirm}
                                onChange={handlePasswordConfirmChange}
                                placeholder="비밀번호를 다시 입력하세요"
                                label="비밀번호 확인"
                                required
                                disabled={isLoading}
                                error={fieldErrors.passwordConfirm}
                            />

                            {/* 휴대폰번호 */}
                            <FormInput
                                id="phoneNum"
                                name="phoneNum"
                                type="tel"
                                value={phoneNum}
                                onChange={handlePhoneNumChange}
                                placeholder="숫자만 입력 (10~12자리)"
                                label="휴대폰번호"
                                required
                                disabled={isInfoFieldDisabled}
                                icon={<PhoneIcon />}
                                error={fieldErrors.phoneNum}
                            />

                            {/* 이메일 (선택) */}
                            <FormInput
                                id="emailInfo"
                                name="emailInfo"
                                type="email"
                                value={emailInfo}
                                onChange={handleEmailChange}
                                placeholder="example@vroong.com (선택사항)"
                                label="이메일"
                                disabled={isLoading}
                                icon={<EmailIcon />}
                                error={fieldErrors.emailInfo}
                            />

                            {/* 에러 메시지 */}
                            {error && <div className="error-message">{error}</div>}

                            <PrimaryButton
                                type="submit"
                                loading={isLoading}
                                disabled={isLoading || !supplierIdChecked}
                            >
                                회원가입
                            </PrimaryButton>
                        </form>

                        {/* 추가 옵션 */}
                        <div className="additional-options">
                            <div className="divider">
                                <span>또는</span>
                            </div>

                            <div className="help-links" style={HELP_LINKS_STYLE}>
                                <LinkButton onClick={handleNavigateToRegister}>
                                    <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                                    </svg>
                                    계정 유형 다시 선택
                                </LinkButton>
                                <LinkButton onClick={handleNavigateToLogin}>
                                    <LoginIcon width={16} height={16} />
                                    로그인 페이지로
                                </LinkButton>
                            </div>
                        </div>
                    </div>

                    {/* 하단 버전 정보 */}
                    <VersionInfo />
                </div>
            </div>
        </div>
    );
};

export default SupplierRegisterPage;
